const DEFAULT_QUERY_KEYS = ['aff', 'affiliate_code'] as const
const DEFAULT_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

export const AFFILIATE_COOKIE_NAME = (process.env.NEXT_PUBLIC_AFFILIATE_COOKIE || 'affiliate_code').trim()
export const AFFILIATE_COOKIE_MAX_AGE = 60 * 60 * 24 * 90 // 90 days in seconds
export const UTM_COOKIE_NAME = 'affiliate_utm'

const bannerEnv = (process.env.NEXT_PUBLIC_AFFILIATE_BANNER ?? 'true').toLowerCase()
export const AFFILIATE_BANNER_ENABLED = bannerEnv !== 'false' && bannerEnv !== '0'

function getSecureAttribute(): string {
  if (typeof window === 'undefined') return ''
  try {
    return window.location.protocol === 'https:' ? '; Secure' : ''
  } catch {
    return ''
  }
}

export function getAffiliateQueryKeys(): string[] {
  const envValue = process.env.NEXT_PUBLIC_AFFILIATE_QUERY_KEYS
  if (!envValue) {
    return [...DEFAULT_QUERY_KEYS]
  }

  const keys = envValue
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter((part) => /^[a-z0-9_-]+$/.test(part))
    .filter((part, index, array) => part && array.indexOf(part) === index)
    .map((part) => part)

  return keys.length > 0 ? keys : [...DEFAULT_QUERY_KEYS]
}

export function sanitizeAffiliateCode(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  const normalized = trimmed.toUpperCase()
  return /^[A-Z0-9_-]+$/.test(normalized) ? normalized : null
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (const entry of cookies) {
    const [key, ...rest] = entry.split('=')
    if (key === name) {
      return decodeURIComponent(rest.join('='))
    }
  }
  return null
}

function writeCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return
  const secure = getSecureAttribute()
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
}

export function getStoredAffiliateCode(): string | null {
  const cookieValue = sanitizeAffiliateCode(readCookie(AFFILIATE_COOKIE_NAME))
  if (cookieValue) {
    return cookieValue
  }
  if (typeof window !== 'undefined') {
    try {
      const sessionValue = window.sessionStorage.getItem(AFFILIATE_COOKIE_NAME)
      return sanitizeAffiliateCode(sessionValue)
    } catch {
      return null
    }
  }
  return null
}

export function setAffiliateCodeStorage(code: string): void {
  const sanitized = sanitizeAffiliateCode(code)
  if (!sanitized) return
  writeCookie(AFFILIATE_COOKIE_NAME, sanitized, AFFILIATE_COOKIE_MAX_AGE)
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(AFFILIATE_COOKIE_NAME, sanitized)
    } catch {
      /* ignore storage errors */
    }
  }
}

export function clearAffiliateCodeStorage(): void {
  deleteCookie(AFFILIATE_COOKIE_NAME)
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(AFFILIATE_COOKIE_NAME)
    } catch {
      /* ignore storage errors */
    }
  }
}

export function parseAffiliateCodeFromParams(searchParams: URLSearchParams): string | null {
  for (const key of getAffiliateQueryKeys()) {
    const value = searchParams.get(key)
    const sanitized = sanitizeAffiliateCode(value)
    if (sanitized) {
      return sanitized
    }
  }
  return null
}

export function parseUtmParams(searchParams: URLSearchParams): Record<string, string> {
  const result: Record<string, string> = {}
  for (const key of DEFAULT_UTM_KEYS) {
    const value = searchParams.get(key)
    if (value && value.trim().length > 0) {
      result[key] = value.trim()
    }
  }
  return result
}

export function getStoredUtmParams(): Record<string, string> {
  const cookieValue = readCookie(UTM_COOKIE_NAME)
  if (cookieValue) {
    const parsed = parseUtmCookieValue(cookieValue)
    if (Object.keys(parsed).length > 0) {
      return parsed
    }
  }

  if (typeof window !== 'undefined') {
    try {
      const raw = window.sessionStorage.getItem(UTM_COOKIE_NAME)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          const entries = Object.entries(parsed).filter(
            (entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string'
          )
          if (entries.length > 0) {
            return Object.fromEntries(entries)
          }
        }
      }
    } catch {
      /* ignore storage errors */
    }
  }

  return {}
}

export function storeUtmParams(params: Record<string, string>): void {
  const entries = Object.entries(params).filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
  if (entries.length === 0) return

  const data = Object.fromEntries(entries.map(([key, value]) => [key, value.trim()]))
  writeCookie(UTM_COOKIE_NAME, JSON.stringify(data), AFFILIATE_COOKIE_MAX_AGE)
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(UTM_COOKIE_NAME, JSON.stringify(data))
    } catch {
      /* ignore storage errors */
    }
  }
}

function parseUtmJson(value: string): Record<string, string> {
  try {
    const parsed = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object') return {}
    const validEntries = Object.entries(parsed).filter(
      (entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string'
    )
    return Object.fromEntries(validEntries)
  } catch {
    return {}
  }
}

export function parseUtmCookieValue(cookieValue: string | null | undefined): Record<string, string> {
  if (!cookieValue) return {}
  const decodedFirst = (() => {
    try {
      return decodeURIComponent(cookieValue)
    } catch {
      return cookieValue
    }
  })()
  const parsed = parseUtmJson(decodedFirst)
  if (Object.keys(parsed).length > 0) {
    return parsed
  }
  return parseUtmJson(cookieValue)
}
